import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthContext, useAuth } from "../context/auth";

import WelcomeModal from "../Modals/welcomeModal/WelcomeModal";
import "./ProfilePicturePage.css";
import { addGuestToEvent } from "../helper/addGuestToEvent";

import { uploadImageToBlob } from "../helper/convertToBase64";

const ProfilePicturePage = ({
  onProceed,
  onBack,
  source: sourceProp,
  onBecomeHostComplete,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const source = sourceProp || location.state?.source;

  const authCtx = useContext(AuthContext);
  const { user, setUser, authenticate } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const [imageSubmitted, setImageSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [showBecomeHostModal, setShowBecomeHostModal] = useState(false);

  const { userData, inviteCode, hostId, userIdForOtpModal } =
    location.state || {};
  const selectedRole = userData?.role;
  const userId = userData?._id || user?._id || "";

  useEffect(() => {
    if (user?.userPhoto && !profileImage) {
      setProfileImage({
        uri: user.userPhoto,
        existing: true,
      });
    }
  }, [user?.userPhoto]);

  useEffect(() => {
    if (userData) {
      const updatedUser = {
        ...user,
        userId: userData._id || userData.userId || user._id || "",
        firstName: userData.firstName || user.firstName || "",
        lastName: userData.lastName || user.lastName || "",
        email: userData.email || user.email || "",
        phoneNumber: userData.phoneNumber || user.phoneNumber || "",
        role: [selectedRole || user?.role],
        isVerified: userData.isVerified || user.isVerified || false,
        isPhoneVerified: userData.isPhoneVerified || user.isVerified || false,
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  }, [userData, setUser, selectedRole]);

  const handleWelcomeComplete = async (action) => {
    setIsProcessing(true);

    const fullName = `${userData?.firstName || user?.firstName || ""} ${
      userData?.lastName || user?.lastName || ""
    }`.trim();

    const emailToUse = userData?.email || user?.email || "";
    const passwordToUse = userData?.password || user?.password || "";

    try {
      const loginResponse = await axios.post("/user/login", {
        email: emailToUse,
        password: passwordToUse,
      });

      const { user, token, refreshToken } = loginResponse?.data?.data;

      const userWithActiveRole = {
        ...user,
        activeRole: selectedRole,
      };

      authCtx.authenticate(token, userWithActiveRole, refreshToken);

      authCtx.setUser((prevUser) => ({
        ...prevUser,
        ...userWithActiveRole,
      }));

      localStorage.setItem("authToken", token);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("userEmail", userData.email);

      setIsWelcomeModalOpen(false);

      if (action === "createEvent") {
        navigate("/home/host", {
          state: { userName: fullName, user: userData },
        });
      } else if (action === "joinEvent" && selectedRole === "guest") {
        setLoading(true);
        const guestResponse = await addGuestToEvent(
          inviteCode,
          hostId,
          userData?._id
        );
        if (guestResponse.error) {
          showAlert(
            "Event Join Failed!",
            guestResponse.isAlreadyInvited
              ? "You are already invited to this event"
              : guestResponse.message || "Failed to add guest to event"
          );
        } else {
          navigate("/invites?reload=true", {
            state: {
              userName: fullName,
              user: { ...userData, userId: userData._id },
              inviteCode: inviteCode,
              hostId: hostId,
            },
          });
        }
      } else {
        if (selectedRole === "guest") {
          navigate("/home/guest", {
            state: { userName: fullName, user: userData },
          });
        } else {
          navigate("/home/host", {
            state: { userName: fullName, user: userData },
          });
        }
      }
    } catch (error) {
      // console.error("Login error:", error);
      showAlert(
        "Login Failed",
        error?.response?.data?.message || "Please try again"
      );
    } finally {
      setLoading(false);
      setIsProcessing(false);
    }
  };

  const showAlert = (title, message) => {
    alert(`${title}: ${message}`);
  };
  const showImagePickerOptions = () => {
    setShowOptions(true);
  };

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 800;
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              resolve(URL.createObjectURL(blob));
            },
            "image/jpeg",
            0.7
          );
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        alert("File size should be less than 2MB");
        return;
      }
      const compressedImageUrl = await compressImage(file);
      const imageObject = {
        uri: compressedImageUrl,
        type: file.type || "image/jpeg",
        name: file.name || `photo_${Date.now()}.jpg`,
        file: file,
      };
      setProfileImage(imageObject);
      setShowOptions(false);
    } catch (error) {
      // console.error("File selection error:", error);
      alert("Failed to select image. Please try again.");
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const uploadImageToBlobLocal = async (base64Image, fileName) => {
    return {
      data: {
        Location: URL.createObjectURL(
          await fetch(base64Image).then((r) => r.blob())
        ),
      },
    };
  };

  const handleSubmit = async () => {
    if (!profileImage || !profileImage.file) {
      alert("Please select an image before proceeding.");
      return;
    }
    const maxRetries = 1;
    let attempts = 0;
    setLoading(true);
    try {
      while (attempts < maxRetries) {
        attempts += 1;
        const base64Image = await convertToBase64(profileImage.file);

        const blobResponse = await uploadImageToBlob(
          base64Image,
          `${user?.firstName || userData?.firstName}-${
            user?.lastName || userData?.lastName
          }`
        );
        const blobUrl = blobResponse?.data?.Location;
        if (!blobUrl) {
          alert("Upload Failed: Unable to upload the image. Try again.");
          setLoading(false);
          return;
        }
        try {
          const response = await axios.patch(
            `/user/${userId}`,
            { userPhoto: blobUrl },
            { headers: { "Content-Type": "application/json" } }
          );
          if (response?.data?.error) {
            if (attempts === maxRetries) {
              alert("Failed to update profile photo: " + response?.data?.error);
            }
          } else {
            const { userPhoto } = response.data.data;

            if (source === "BECOME-HOST") {
              const updatedRoles = Array.from(
                new Set([...(user?.role || []), "host"])
              );

              const updatedUser = {
                ...user,
                userPhoto,
                role: updatedRoles,
              };

              await axios.patch(
                `/user/${userId}`,
                { role: updatedRoles },
                { headers: { "Content-Type": "application/json" } }
              );

              setUser(updatedUser);
              localStorage.setItem("user", JSON.stringify(updatedUser));

              setShowBecomeHostModal(true);
            } else {
              setUser((prevUser) => ({ ...prevUser, userPhoto }));
              setIsWelcomeModalOpen(true);
            }

            setImageSubmitted(true);
            break;
          }
        } catch (error) {
          if (attempts === maxRetries) {
            alert("Upload failed. Please try again.");
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/signup", {
      state: {
        userData,
        inviteCode,
        hostId,
      },
    });
  };

  const handleProceed = async () => {
    if (profileImage?.existing) {
      if (source === "BECOME-HOST") {
        const updatedRoles = Array.from(
          new Set([...(user?.role || []), "host"])
        );

        const updatedUser = {
          ...user,
          role: updatedRoles,
        };

        await axios.patch(
          `/user/${userId}`,
          { role: updatedRoles },
          { headers: { "Content-Type": "application/json" } }
        );

        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));

        setShowBecomeHostModal(true);
        return;
      }
    }

    if (profileImage && !imageSubmitted) {
      handleSubmit();
    } else {
      setIsWelcomeModalOpen(true);
    }
  };

  return (
    <div className="profile-page-container">
      <div className="profile-img-container">
        <img
          src="/images/onboarding-page-image.webp"
          alt="NimbleBash celebration"
          className="profile-celebration-img"
        />
      </div>

      <div
        className={`${source !== "BECOME-HOST" ? "profile-page-content" : ""}`}
      >
        <div
          className={`${
            source !== "BECOME-HOST"
              ? "profile-form-section"
              : "profile-form-section-become-host"
          }`}
        >
          <div className="profile-form-content">
            {source !== "BECOME-HOST" && (
              <h1 className="owanbe-logo">NIMBLEBASH</h1>
            )}

            {source === "BECOME-HOST" ? (
              <h5 className="profile-title">
                {profileImage?.existing
                  ? "Your profile picture is already set"
                  : "To become a host, please upload a clear profile picture"}
              </h5>
            ) : (
              <h2 className="profile-title">
                Welcome, {user?.firstName || userData?.firstName || "User"}.
                Let&apos;s set you up and get you ready!
              </h2>
            )}

            <div className="upload-label">
              {profileImage?.existing ? (
                <>Profile Picture</>
              ) : (
                <>
                  Upload Profile Picture{" "}
                  <span className="required-mark">*</span>
                </>
              )}
            </div>

            <div className="upload-section">
              <div
                className="ppp-avatar-container"
                onClick={
                  profileImage?.existing ? undefined : showImagePickerOptions
                }
                role="button"
                aria-disabled={profileImage?.existing}
              >
                {profileImage && profileImage.uri ? (
                  <>
                    <img
                      src={profileImage.uri}
                      className="ppp-avatar"
                      alt="Profile"
                    />
                    {!profileImage?.existing && (
                      <div className="photo-overlay change-photo">
                        <span>Change</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="ppp-avatar-placeholder">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="upload-instructions">
                <button
                  className="select-image-button"
                  disabled={profileImage?.existing}
                  onClick={() => document.getElementById("file-input").click()}
                >
                  Select an image
                </button>

                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />

                <p className="helper-text">
                  {profileImage?.existing
                    ? "You can continue using your existing profile picture."
                    : "Make sure the file size is below 2MB."}
                </p>
              </div>
            </div>

            <div className="note-container">
              <p className="note-text">
                {profileImage?.existing
                  ? "You can update your profile picture anytime from your account settings. Kindly click continue to complete the application"
                  : "Kindly upload a clear picture — it increases your chances of approval."}
              </p>
            </div>

            <div className="profile-page-footer">
              {source !== "ONBOARDING" && source !== "BECOME-HOST" && (
                <button className="back-button" onClick={handleBack}>
                  Back
                </button>
              )}

              <button
                className={`proceed-button ${
                  !profileImage ? "proceed-button-disabled" : ""
                }`}
                onClick={handleProceed}
                disabled={loading || isProcessing || !profileImage}
              >
                {profileImage?.existing
                  ? "Continue"
                  : source === "BECOME-HOST"
                    ? "Upload"
                    : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loader"></div>
        </div>
      )}

      {showOptions && (
        <div className="options-overlay" onClick={() => setShowOptions(false)}>
          <div
            className="options-container"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="options-title">
              {profileImage && profileImage.uri
                ? "Change Profile Picture"
                : "Select Image Source"}
            </h3>
            <div className="options-divider"></div>
            <button
              className="option-button"
              onClick={() => document.getElementById("file-input").click()}
            >
              Choose from Files
            </button>
            <button
              className="option-button cancel-button"
              onClick={() => setShowOptions(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <WelcomeModal
        isOpen={isWelcomeModalOpen}
        onClose={() => setIsWelcomeModalOpen(false)}
        userName={`${userData?.firstName || user?.firstName || ""} ${
          userData?.lastName || user?.lastName || ""
        }`.trim()}
        inviteCode={inviteCode}
        hostId={hostId}
        role={selectedRole}
        isProcessing={isProcessing}
        buttons={{
          primary: selectedRole === "guest" ? "Go to Invites" : "Create Event",
          primaryAction: () =>
            handleWelcomeComplete(
              selectedRole === "guest" ? "joinEvent" : "createEvent"
            ),
          secondary: selectedRole === "host" ? "Home" : null,
          secondaryAction: () => {
            setIsWelcomeModalOpen(false);
          },
        }}
        userData={userData}
      />

      {showBecomeHostModal && (
        <div className="profile-img-modal-overlay">
          <div className="profile-img-modal-container">
            <h3>Host Role Added</h3>
            <p style={{ textAlign: "left" }}>
              <br /> The <strong>host</strong> role has been added successfully.
              <br /> As host, you can: <br />
              <br />
              <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
                <li>Create and manage your own events</li>
                <li>Invite guests and assign ushers</li>
                <li>Download curated lists of invited guests</li>
                <li>Receive event attire and other payments with ease</li>
              </ul>
            </p>

            <p style={{ textAlign: "left" }}>
              You can now switch roles using the
              <strong> Switch Account</strong> tab.
            </p>
            <button
              className="profile-img-primary-button"
              onClick={() => {
                setShowBecomeHostModal(false);
                onBecomeHostComplete?.();
              }}
            >
              Go to Switch Account
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePicturePage;
